/**
 * Stripe legal agreement and documentation URLs
 */
export const STRIPE_URLS = {
    /** Stripe homepage */
    HOME: 'https://stripe.com/',

    /** Stripe Services Agreement */
    SERVICES_AGREEMENT: 'https://stripe.com/legal/ssa',

    /** Stripe Connected Account Agreement */
    CONNECTED_ACCOUNT_AGREEMENT: 'https://stripe.com/legal/connect-account',

    /** Issuing Bank Terms (Celtic Bank) */
    ISSUING_BANK_TERMS: 'https://stripe.com/legal/celtic-spend-card',

    /** Stripe E-Sign consent policy */
    E_SIGN_POLICY: 'https://stripe.com/legal/e-sign-consent',
} as const;

/**
 * Connect account onboarding status values returned by the API.
 */
export const ONBOARDING_STATUSES = {
    VERIFIED: 'verified',
    PENDING: 'pending',
    INCOMPLETE: 'incomplete',
    RESTRICTED: 'restricted',
    NOT_STARTED: 'not_started',
    ERROR: 'error',
} as const;

/**
 * SD-11767: which layout Financial Accounts draws. LOADING until the Treasury status can say, so the
 * page never draws one layout and then swaps to the other.
 */
export const TREASURY_PAGE_VIEWS = {
    LOADING: 'loading',
    SETUP: 'setup',
    DASHBOARD: 'dashboard',
} as const;

export type TreasuryPageView = typeof TREASURY_PAGE_VIEWS[keyof typeof TREASURY_PAGE_VIEWS];

/**
 * SD-10158: whether the org still has verification to do, so offering "Verify now" is an action it can
 * take. Not PENDING or RESTRICTED (SD-8351: done and waiting, or told to contact support), and not a
 * status we failed to read.
 */
export function isVerificationActionable(status: string | null): boolean {
    return status === ONBOARDING_STATUSES.NOT_STARTED || status === ONBOARDING_STATUSES.INCOMPLETE;
}

/**
 * Stripe Connect embedded component appearance configuration.
 * Centralizes PayHOA brand colors and typography for the Stripe onboarding UI.
 */
export const STRIPE_APPEARANCE_CONFIG = {
    variables: {
        colorPrimary: '#2a6be6',
        colorBackground: '#ffffff',
        colorText: '#1b1e23',
        colorDanger: '#df1b41',
        fontFamily: '"Proxima Nova", system-ui, sans-serif',
        fontSizeBase: '18px',
        borderRadius: '8px',
        spacingUnit: '10px',
    },
} as const;

/**
 * Stripe-provided brand assets bundled in the app.
 */
export const STRIPE_ASSETS = {
    /** Stripe's official "Powered by Stripe" badge (blurple outlined pill, 300x68). */
    POWERED_BY_BADGE: '/assets/images/app/bank-logos/powered-by-stripe-300x68.png',
    /** Stripe's official wordmark, for a lockup built beside our own type. Never recolored. */
    WORDMARK: '/assets/images/app/bank-logos/stripe-wordmark.svg',
} as const;

/**
 * User-facing display name of the org's Stripe Treasury financial account.
 *
 * SD-8703 renamed this from "PayHOA Balance" to "Treasury Balance". Kept here so the TypeScript
 * sites that build the label themselves (rather than reading `friendlyName` off the API) share one
 * source of truth. Templates that render the bare name inline stay literal, per the interpolation
 * convention. NOTE: this is the label for the SPENDABLE FUNDS — it is NOT the feature name
 * "Financial Accounts", which is unchanged.
 */
export const TREASURY_ACCOUNT_NAME = 'Treasury Balance';

/**
 * SD-8351: why "add a bank account" is unavailable, for the two Connect statuses that cannot be
 * advanced by (re-)running KYC.
 *
 * Single source of truth because two surfaces render it from different layers — the shared
 * `FinancialConnectionsService` (for callers with no inline slot) and the settings Financial
 * Accounts page (inline notice + wrapper tooltip) — and near-identical copy in two files drifts.
 *
 * The two statuses need genuinely different titles, not one shared heading: a PENDING org's
 * verification really is in progress, whereas a RESTRICTED org's did NOT succeed, and titling that
 * "Verification in progress" tells them to keep waiting when they need to contact support.
 */
export const TREASURY_VERIFICATION_BLOCKED_COPY = {
    pending: {
        title: 'Verification in progress',
        message: 'We are still verifying your details. You will be able to add accounts once verification is complete.',
    },
    restricted: {
        title: 'Verification unsuccessful',
        message: 'We were not able to verify some of your information. Please contact support to finish setting up your financial account.',
    },
} as const;

/**
 * The sentences of Stripe's required Treasury and Issuing disclosures, each written once. The
 * Treasury setup page shows them as numbered footnotes; everywhere else shows `PARTNER_DISCLOSURE`,
 * which is built from the same sentences so the two can never say different things.
 */
const STRIPE_DISCLOSURE_PARTS = {
    PARTNER_LEAD: 'PayHOA partners with',
    PARTNER_LINK: 'Stripe Payments Company',
    PARTNER_TAIL: 'for money transmission services and account services with funds held at Fifth Third Bank N.A., Member FDIC.',
    NOT_A_BANK: 'PayHOA is a financial technology company, not a bank or a money transmitter.',
    CARD_PROGRAM: 'PayHOA Visa® Commercial cards are powered by Stripe and issued by Celtic Bank.',
} as const;

/**
 * Disclosure statement text for Stripe Treasury dialogs
 */
export const STRIPE_DISCLOSURE_TEXT = {
    ...STRIPE_DISCLOSURE_PARTS,

    /** Standard partner disclosure for Treasury features */
    PARTNER_DISCLOSURE: `${STRIPE_DISCLOSURE_PARTS.PARTNER_LEAD} <a href="${STRIPE_URLS.HOME}" target="_blank" rel="noopener">${STRIPE_DISCLOSURE_PARTS.PARTNER_LINK}</a> ${STRIPE_DISCLOSURE_PARTS.PARTNER_TAIL} ${STRIPE_DISCLOSURE_PARTS.CARD_PROGRAM}`,

    /**
     * SD-8703: Stripe-compliance explanation shown on the "Powered by Stripe" badge beside the
     * Treasury Balance heading. Makes explicit that the funds sit with Stripe, not PayHOA.
     * Copy is approved verbatim — do not reword.
     */
    TREASURY_ACCOUNT_EXPLANATION: `Your community's financial account is provided through Stripe Treasury. PayHOA never holds or has access to your funds.`,
} as const;

/**
 * SD-8663: reason shown when a Financial Accounts Control has switched a capability off.
 *
 * These controls are enforced server-side only, so without an explanation on the disabled CTA the
 * user has no way to tell a missing permission from a deliberate org-level lockout.
 *
 * Wording matches the app's existing convention for an org-level capability being off — compare
 * add-bill-pay-account ("Please contact PayHOA to enable creation of bill pay accounts for this
 * organization") and plaid-search ("Please contact PayHOA to enable manual account linking"). Keep
 * this in step with StoreCardRequest::ISSUANCE_DISABLED_MESSAGE, which the API returns on the 403.
 */
export const SPEND_CARDS_DISABLED_TOOLTIP = 'Please contact PayHOA to enable spend card issuance for this organization.';

/**
 * SD-8663: reason shown when the super-admin "Existing Spend Cards" control is switched OFF, which
 * freezes every card account-wide and refuses any action that would produce a spendable card.
 *
 * Two strings rather than one because the two controls fail for different reasons and an admin needs
 * to know which: "not enabled yet" is a capability question, "frozen" is an active lockout. Keep
 * these in step with ExistingCardLockout::BLOCKED_MESSAGE and ISSUANCE_BLOCKED_MESSAGE, which the
 * API returns on the corresponding 403s.
 */
export const EXISTING_SPEND_CARDS_LOCKED_TOOLTIP = 'Spend cards are currently frozen for this organization and cannot be unfrozen. Please contact PayHOA for more information.';

/** As above, for the actions that would create a card rather than revive one. */
export const EXISTING_SPEND_CARDS_LOCKED_ISSUANCE_TOOLTIP = 'Spend cards are currently frozen for this organization, so new cards cannot be issued. Please contact PayHOA for more information.';

/**
 * Why the Change PIN tile is disabled for everyone except the card's own cardholder (SD-8111).
 *
 * Shown rather than hiding the tile so an admin a cardholder has asked for help understands why
 * they cannot do it, instead of hunting for a button that was never there. Kept in step with
 * `CardholderException::pinChangeNotPermitted()`, which the API returns verbatim on the 403.
 */
export const CHANGE_PIN_BLOCKED_TOOLTIP = 'This can only be changed by the card holder.';

/**
 * SD-9498: the admin-facing label for each Stripe field PayHOA can withhold from a Connect
 * account create.
 *
 * Keyed on the API's own `field` values (Stripe's dotted names), because those are what the
 * preflight endpoint returns and what `requirements.currently_due` uses. A field with no entry
 * here is deliberately not rendered — the omission is still logged and audited server-side, and
 * showing a raw `company.tax_id` to an admin is worse than showing nothing.
 *
 * The notice names the FIELD, never the stored value: the value adds nothing actionable (the
 * admin is being sent to the page that displays it) and echoing profile data into a modal puts it
 * into every screenshot and screen-share of this flow.
 */
export const PREFILL_FIELD_LABELS: Record<string, string> = {
    'company.address': 'Organization street address',
    'company.address.line2': 'Organization address line 2',
    'company.address.state': 'Organization state',
    'company.address.postal_code': 'Organization ZIP code',
    'company.tax_id': 'EIN',
    'company.phone': 'Organization phone number',
    'email': 'Organization email address',
    'business_profile.url': 'Organization website',
    'person': 'Your name, for the verification representative',
    'person.email': 'Your email address',
    'person.phone': 'Your phone number',
    'person.address': 'Your street address',
} as const;

/**
 * SD-9498: the pre-verification notice shown when some stored details cannot be pre-filled.
 *
 * Informational, never blocking — "Continue to verification" is the primary action. Blocking here
 * would recreate the hard stop this ticket exists to remove.
 */
export const PREFILL_NOTICE_COPY = {
    title: 'Some saved details can\'t be used for verification',
    intro: 'Stripe won\'t accept the following from your organization profile, so we\'ve left them out and Stripe will ask you for them directly:',
    outro: 'You can continue now and enter them in Stripe\'s form, or update them in your organization settings first.',
    confirmButton: 'Continue to verification',
    secondaryButton: 'Review organization settings',
} as const;

/**
 * The field names the notice lists, from the preflight response, or none when there is nothing an
 * admin can act on — the caller treats that as "no notice" and launches verification directly, so
 * the happy path opens exactly the dialogs it did before.
 *
 * Every string is a fixed label from the map above, never stored organization data.
 */
export function prefillNoticeLabels(omissions: readonly { field: string }[]): string[] {
    return [...new Set(
        omissions
            .map((omission) => PREFILL_FIELD_LABELS[omission.field])
            .filter((label): label is string => !!label),
    )];
}

/**
 * Cardholder status values
 */
export const CARDHOLDER_STATUSES = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    LISTED: 'listed',
    BLOCKED: 'blocked',
} as const;

/**
 * Card status values
 */
export const CARD_STATUSES = {
    INACTIVE: 'inactive',
    ACTIVE: 'active',
    CANCELED: 'canceled',
} as const;

/**
 * Maps a card status to an AppStatusChipComponent color.
 */
export function getCardStatusColor(status: string): string {
    switch (status) {
        case CARD_STATUSES.ACTIVE:
            return 'success';
        case CARD_STATUSES.INACTIVE:
            return 'info';
        case 'expired':
            return 'warn';
        case 'replaced':
        case CARD_STATUSES.CANCELED:
            return 'error';
        default:
            return 'default';
    }
}

/**
 * Card type values
 */
export const CARD_TYPES = {
    VIRTUAL: 'virtual',
    PHYSICAL: 'physical',
} as const;

/**
 * Physical card shipping statuses from Stripe
 */
export const SHIPPING_STATUSES = {
    PENDING: 'pending',
    SHIPPED: 'shipped',
    DELIVERED: 'delivered',
    RETURNED: 'returned',
    FAILURE: 'failure',
} as const;

/**
 * Physical card shipping service tiers from Stripe.
 */
export const SHIPPING_SERVICES = {
    STANDARD: 'standard',
    EXPRESS: 'express',
    PRIORITY: 'priority',
} as const;

/**
 * Whether a shipping tier produces tracking information at all.
 *
 * Standard ships untracked, so its status never advances past `pending`.
 * An unknown tier returns false — never promise tracking we can't prove exists.
 */
export function isTrackedShippingService(service: string | null | undefined): boolean {
    switch (service) {
        case SHIPPING_SERVICES.EXPRESS:
        case SHIPPING_SERVICES.PRIORITY:
            return true;
        default:
            return false;
    }
}

/**
 * Maps a shipping status to an AppStatusChipComponent color.
 */
export function getShippingStatusColor(status: string): string {
    switch (status) {
        case SHIPPING_STATUSES.DELIVERED:
            return 'success';
        case SHIPPING_STATUSES.SHIPPED:
            return 'info';
        case SHIPPING_STATUSES.PENDING:
            return 'default';
        case SHIPPING_STATUSES.RETURNED:
            return 'warn';
        case SHIPPING_STATUSES.FAILURE:
            return 'error';
        default:
            return 'default';
    }
}

/**
 * Maps a shipping status to a user-friendly label.
 */
export function getShippingStatusLabel(status: string): string {
    switch (status) {
        case SHIPPING_STATUSES.PENDING:
            return 'Pending';
        case SHIPPING_STATUSES.SHIPPED:
            return 'Shipped';
        case SHIPPING_STATUSES.DELIVERED:
            return 'Delivered';
        case SHIPPING_STATUSES.RETURNED:
            return 'Returned';
        case SHIPPING_STATUSES.FAILURE:
            return 'Failed';
        default:
            return status;
    }
}

/**
 * Maps a cardholder status to an AppStatusChipComponent color (mirrors the
 * Cardholders list). Pending-activation nuance is caller-specific and not
 * handled here.
 */
export function getCardholderStatusColor(status: string): string {
    switch (status) {
        case CARDHOLDER_STATUSES.ACTIVE:
            return 'success';
        case CARDHOLDER_STATUSES.LISTED:
            return 'warn';
        case CARDHOLDER_STATUSES.BLOCKED:
            return 'error';
        case CARDHOLDER_STATUSES.INACTIVE:
            return 'info';
        default:
            return 'default';
    }
}

/**
 * Maps a cardholder status to a display label (mirrors the Cardholders list).
 */
export function getCardholderStatusLabel(status: string): string {
    switch (status) {
        case CARDHOLDER_STATUSES.ACTIVE:
            return 'Active';
        case CARDHOLDER_STATUSES.LISTED:
            return 'Under Review';
        case CARDHOLDER_STATUSES.BLOCKED:
            return 'Blocked';
        case CARDHOLDER_STATUSES.INACTIVE:
            return 'Inactive';
        default:
            return status;
    }
}

/**
 * Spending limit interval options
 */
export const SPENDING_LIMIT_INTERVALS = {
    PER_AUTHORIZATION: 'per_authorization',
    DAILY: 'daily',
    WEEKLY: 'weekly',
    MONTHLY: 'monthly',
    YEARLY: 'yearly',
    ALL_TIME: 'all_time',
} as const;

/**
 * Human-readable labels for spending limit intervals
 */
export const SPENDING_LIMIT_INTERVAL_LABELS: Record<string, string> = {
    per_authorization: 'Per Transaction',
    daily: 'Per Day',
    weekly: 'Per Week',
    monthly: 'Per Month',
    yearly: 'Per Year',
    all_time: 'All Time',
} as const;

/**
 * Dispute status values
 */
export const DISPUTE_STATUSES = {
    SUBMITTED: 'submitted',
    WON: 'won',
    LOST: 'lost',
    EXPIRED: 'expired',
    UNSUBMITTED: 'unsubmitted',
} as const;

/**
 * Typical Stripe Issuing dispute resolution window, shown wherever we tell a user that funds
 * are held during the investigation. Single source so the range stays consistent across every
 * surface (cardholder recent activity, dashboard, spend-cards table). SD-7533.
 *
 * Value per Stripe docs: "This process typically takes between 30 and 90 days."
 * https://docs.stripe.com/issuing/purchases/disputes — corrects the prior inconsistent and
 * undocumented copy (cardholder/dashboard said 55–120, spend-cards table said 45–90).
 */
export const DISPUTE_RESOLUTION_WINDOW = '30–90 days';

/**
 * Maps a dispute status to an AppStatusChipComponent color.
 */
/** The kit status badge's `status` for one of the colour names the helpers above return. */
const BADGE_STATUS_BY_COLOR: Record<string, 'completed' | 'pending' | 'failed' | 'disabled' | 'info'> = {
    success: 'completed',
    info: 'info',
    warn: 'pending',
    error: 'failed',
    default: 'disabled',
};

export function badgeStatusForColor(color: string): 'completed' | 'pending' | 'failed' | 'disabled' | 'info' {
    return BADGE_STATUS_BY_COLOR[color] ?? 'disabled';
}

export function getDisputeStatusColor(status: string): string {
    switch (status) {
        case DISPUTE_STATUSES.SUBMITTED:
            return 'warn';
        case DISPUTE_STATUSES.WON:
            return 'success';
        case DISPUTE_STATUSES.LOST:
            return 'error';
        case DISPUTE_STATUSES.EXPIRED:
            return 'default';
        case DISPUTE_STATUSES.UNSUBMITTED:
            return 'info';
        default:
            return 'default';
    }
}

/**
 * US state options for address forms
 */
export const US_STATES: { value: string; label: string }[] = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
    { value: 'DC', label: 'District of Columbia' },
];

/**
 * SD-10387. Core's machine-readable reason for the one Link-account refusal an admin can clear.
 *
 * Read as a `reason`, never as a status: this arrives as a 422, and so does SD-10187's flat last4
 * mismatch, which must NOT be offered a confirmation — confirming it would re-submit a pairing core
 * has already disproven. Must stay identical to
 * `FinancialConnectionsAccountMismatchException::REASON_AMBIGUOUS_LAST4`.
 */
export const FC_AMBIGUOUS_LAST4_REASON = 'fc_account_ambiguous_last4';

export const FC_AMBIGUOUS_LAST4_COPY = {
    title: 'Which account is this?',
    confirmButton: 'Yes, link this account',
    /**
     * A SECONDARY action, not the cancel one, and it has to actually re-open the bank picker.
     * Observed 2026-09-28: as a cancel button it simply dismissed the dialog, leaving an admin who
     * had been told to "choose the other account at your bank" to rediscover the seven-click journey
     * on their own. A button that names an action must perform it.
     */
    secondaryButton: 'Choose a different account',
    cancelButton: 'Cancel',
    /**
     * Only ever shown if core refused for this reason but served no message. Core always sends one
     * — and its wording NAMES both accounts, which is the whole remedy — so this is the shape that
     * must not become a dead end rather than the copy anyone should see. Deliberately vague: the
     * two account names are the one thing we cannot invent here.
     */
    fallbackContent: 'More than one of your bank accounts ends in these digits, so we cannot tell '
        + 'them apart on the account number alone. Only continue if the account you just picked at '
        + 'your bank is this same bank account.',
};
